import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import SearchesHeader from '../components/SearchesHeader'

export default function History() {
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [analyses, setAnalyses] = useState([])
  const [filteredAnalyses, setFilteredAnalyses] = useState([])
  const [searchKeyword, setSearchKeyword] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [scoreFilter, setScoreFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 15
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [saveName, setSaveName] = useState('')
  const [analysisToSave, setAnalysisToSave] = useState(null)
  const [saveMessage, setSaveMessage] = useState('')

  useEffect(() => {
    checkAuth()
  }, [])

  useEffect(() => {
    applyFilters()
  }, [analyses, searchKeyword, dateFrom, dateTo, scoreFilter])

  async function checkAuth() {
    try {
      const { data: { session } } = await supabase.auth.getSession()

      if (!session) {
        navigate('/login', { replace: true })
        return
      }

      setUser(session.user)
      await loadUserProfile(session.user.id)
      await loadAnalyses(session.user.id)
      setLoading(false)
    } catch (error) {
      console.error('Auth error:', error)
      navigate('/login', { replace: true })
    }
  }

  async function loadUserProfile(userId) {
    try {
      console.log('[HISTORY] Loading profile for user:', userId)
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle()

      if (error) {
        console.error('[HISTORY] Error loading profile:', error)
        return
      }

      if (data) {
        console.log('[HISTORY] Profile loaded:', { role: data.role, id: data.id })
        setUserProfile(data)
      } else {
        console.warn('[HISTORY] No profile found for user:', userId)
      }
    } catch (err) {
      console.error('[HISTORY] Unexpected error loading profile:', err)
    }
  }

  async function loadAnalyses(userId) {
    try {
      console.log('[HISTORY] Loading analyses for user:', userId)
      const { data, error } = await supabase
        .from('analyses')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('[HISTORY] Error loading analyses:', error)
        setAnalyses([])
      } else {
        console.log('[HISTORY] Loaded analyses:', data?.length || 0)
        setAnalyses(data || [])
      }
    } catch (err) {
      console.error('[HISTORY] Unexpected error loading analyses:', err)
      setAnalyses([])
    }
  }

  function applyFilters() {
    let filtered = [...analyses]

    if (searchKeyword.trim()) {
      const keyword = searchKeyword.toLowerCase()
      filtered = filtered.filter(analysis =>
        analysis.job_text?.toLowerCase().includes(keyword) ||
        analysis.result_json?.companies?.some(c => c.name?.toLowerCase().includes(keyword))
      )
    }

    if (dateFrom) {
      filtered = filtered.filter(analysis =>
        new Date(analysis.created_at) >= new Date(dateFrom)
      )
    }

    if (dateTo) {
      const endDate = new Date(dateTo)
      endDate.setHours(23, 59, 59, 999)
      filtered = filtered.filter(analysis =>
        new Date(analysis.created_at) <= endDate
      )
    }

    if (scoreFilter !== 'all') {
      if (scoreFilter === 'high') {
        filtered = filtered.filter(analysis => analysis.confidence_score >= 4)
      } else if (scoreFilter === 'medium') {
        filtered = filtered.filter(analysis => analysis.confidence_score === 3)
      } else if (scoreFilter === 'low') {
        filtered = filtered.filter(analysis => analysis.confidence_score < 3)
      }
    }

    setFilteredAnalyses(filtered)
    setCurrentPage(1)
  }

  async function handleSignOut() {
    await supabase.auth.signOut()
    navigate('/')
  }

  async function handleSaveSearch(analysis) {
    setAnalysisToSave(analysis)
    const topCompany = analysis.result_json?.companies?.[0]?.name || 'Search'
    const date = new Date(analysis.created_at).toLocaleDateString()
    setSaveName(`${topCompany} - ${date}`)
    setShowSaveModal(true)
  }

  async function confirmSaveSearch() {
    if (!saveName.trim() || !analysisToSave) return

    try {
      const { data: existing, error: checkError } = await supabase
        .from('saved_searches')
        .select('id')
        .eq('user_id', user.id)
        .eq('original_analysis_id', analysisToSave.id)
        .maybeSingle()

      if (checkError) throw checkError

      if (existing) {
        setSaveMessage('This analysis is already saved!')
        setTimeout(() => {
          setSaveMessage('')
          setShowSaveModal(false)
          setSaveName('')
          setAnalysisToSave(null)
        }, 2000)
        return
      }

      const { error } = await supabase
        .from('saved_searches')
        .insert({
          user_id: user.id,
          search_name: saveName,
          job_text: analysisToSave.job_text,
          result_json: analysisToSave.result_json,
          saved_from: 'history',
          original_analysis_id: analysisToSave.id,
          is_duplicate: false
        })

      if (error) throw error

      setSaveMessage('Search saved successfully!')
      setTimeout(() => {
        setSaveMessage('')
        setShowSaveModal(false)
        setSaveName('')
        setAnalysisToSave(null)
      }, 1500)
    } catch (error) {
      console.error('Error saving search:', error)
      setSaveMessage('Error saving search. Please try again.')
      setTimeout(() => setSaveMessage(''), 3000)
    }
  }

  const paginatedAnalyses = filteredAnalyses.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )
  const totalPages = Math.ceil(filteredAnalyses.length / itemsPerPage)

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p style={{ marginTop: '20px', color: '#98FB98', fontSize: '14px' }}>Loading search history...</p>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p style={{ marginTop: '20px', color: '#98FB98', fontSize: '14px' }}>Authenticating...</p>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh' }}>
      <div className="nav">
        <div className="nav-content">
          <div
            className="nav-title"
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}
          >
            <span>&gt; AgencySniper</span>
          </div>
          <div className="nav-actions">
            <button onClick={() => navigate('/dashboard')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Dashboard
            </button>
            <button onClick={() => navigate('/saved')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Saved
            </button>
            <button onClick={() => navigate('/agencies')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Agencies
            </button>
            <button onClick={() => navigate('/recent-searches')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Recent
            </button>
            {userProfile?.role === 'admin' && (
              <button onClick={() => navigate('/admin')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
                Admin
              </button>
            )}
            <button onClick={handleSignOut} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <SearchesHeader userId={user?.id} userProfile={userProfile} />

      <div className="container">
        <div className="card">
          <h2 style={{ fontSize: '32px', marginBottom: '16px', fontWeight: '700', color: '#98FB98', textShadow: '0 0 15px rgba(152, 251, 152, 0.7)' }}>
            &gt; Search History
          </h2>
          <p style={{ color: 'rgba(152, 251, 152, 0.85)', marginBottom: '28px', fontSize: '15px', textShadow: '0 0 5px rgba(152, 251, 152, 0.4)' }}>
            Browse and filter all your job posting analyses
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '28px',
            padding: '20px',
            background: 'rgba(34, 139, 34, 0.2)',
            borderRadius: '12px',
            border: '2px solid rgba(152, 251, 152, 0.3)'
          }}>
            <div>
              <label style={{ display: 'block', color: '#98FB98', fontSize: '13px', marginBottom: '6px', fontWeight: '600' }}>
                Search Keyword
              </label>
              <input
                type="text"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                placeholder="Company name or job text"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(152, 251, 152, 0.4)',
                  borderRadius: '8px',
                  color: '#F5F5F5',
                  fontSize: '14px',
                  fontFamily: 'Cousine, monospace'
                }}
                aria-label="Search by keyword"
              />
            </div>

            <div>
              <label style={{ display: 'block', color: '#98FB98', fontSize: '13px', marginBottom: '6px', fontWeight: '600' }}>
                From Date
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(152, 251, 152, 0.4)',
                  borderRadius: '8px',
                  color: '#F5F5F5',
                  fontSize: '14px',
                  fontFamily: 'Cousine, monospace'
                }}
                aria-label="Filter from date"
              />
            </div>

            <div>
              <label style={{ display: 'block', color: '#98FB98', fontSize: '13px', marginBottom: '6px', fontWeight: '600' }}>
                To Date
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(152, 251, 152, 0.4)',
                  borderRadius: '8px',
                  color: '#F5F5F5',
                  fontSize: '14px',
                  fontFamily: 'Cousine, monospace'
                }}
                aria-label="Filter to date"
              />
            </div>

            <div>
              <label style={{ display: 'block', color: '#98FB98', fontSize: '13px', marginBottom: '6px', fontWeight: '600' }}>
                Confidence Score
              </label>
              <select
                value={scoreFilter}
                onChange={(e) => setScoreFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(152, 251, 152, 0.4)',
                  borderRadius: '8px',
                  color: '#F5F5F5',
                  fontSize: '14px',
                  fontFamily: 'Cousine, monospace',
                  cursor: 'pointer'
                }}
                aria-label="Filter by confidence score"
              >
                <option value="all">All Scores</option>
                <option value="high">High (4-5)</option>
                <option value="medium">Medium (3)</option>
                <option value="low">Low (1-2)</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '16px', color: '#98FB98', fontSize: '14px' }}>
            Showing {filteredAnalyses.length} of {analyses.length} total analyses
          </div>

          {filteredAnalyses.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'rgba(152, 251, 152, 0.65)' }}>
              <p style={{ fontSize: '18px', marginBottom: '12px' }}>No analyses found</p>
              <p style={{ fontSize: '14px' }}>
                Try adjusting your filters or run a new analysis from the dashboard
              </p>
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '14px',
                  fontFamily: 'Cousine, monospace'
                }}>
                  <thead>
                    <tr style={{ background: '#2a2a2a', borderBottom: '2px solid rgba(152, 251, 152, 0.3)' }}>
                      <th style={{ padding: '14px 16px', textAlign: 'left', color: '#98FB98', fontWeight: '700' }}>Date</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', color: '#98FB98', fontWeight: '700' }}>Job Snippet</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', color: '#98FB98', fontWeight: '700' }}>Top Match</th>
                      <th style={{ padding: '14px 16px', textAlign: 'center', color: '#98FB98', fontWeight: '700' }}>Score</th>
                      <th style={{ padding: '14px 16px', textAlign: 'center', color: '#98FB98', fontWeight: '700' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedAnalyses.map((analysis) => {
                      const topCompany = analysis.result_json?.companies?.[0]
                      const snippet = analysis.job_text?.substring(0, 100) || 'N/A'

                      return (
                        <tr
                          key={analysis.id}
                          onClick={() => navigate(`/recent-searches?analysis_id=${analysis.id}`)}
                          style={{
                            cursor: 'pointer',
                            background: '#1a1a1a',
                            borderBottom: '1px solid rgba(152, 251, 152, 0.2)',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#252525'
                            e.currentTarget.style.boxShadow = '0 0 15px rgba(0, 0, 0, 0.3)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#1a1a1a'
                            e.currentTarget.style.boxShadow = 'none'
                          }}
                        >
                          <td style={{ padding: '14px 16px', color: '#d0d0d0', whiteSpace: 'nowrap' }}>
                            {new Date(analysis.created_at).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '14px 16px', color: '#d0d0d0', maxWidth: '300px' }}>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {snippet}...
                            </div>
                          </td>
                          <td style={{ padding: '14px 16px', color: '#f5f5f5', fontWeight: '600' }}>
                            {topCompany?.name || 'N/A'}
                          </td>
                          <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                            <span className={`badge ${analysis.confidence_score >= 4 ? 'badge-high' : analysis.confidence_score >= 3 ? 'badge-medium' : 'badge-low'}`}>
                              {analysis.confidence_score}/5
                            </span>
                          </td>
                          <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleSaveSearch(analysis)
                              }}
                              className="btn-secondary"
                              style={{ padding: '6px 12px', fontSize: '12px', whiteSpace: 'nowrap' }}
                            >
                              ⭐ Save
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '12px',
                  marginTop: '28px',
                  padding: '20px'
                }}>
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="btn-secondary"
                    style={{
                      padding: '10px 20px',
                      fontSize: '14px',
                      opacity: currentPage === 1 ? 0.5 : 1,
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                    }}
                    aria-label="Previous page"
                  >
                    Previous
                  </button>
                  <span style={{ color: '#98FB98', fontSize: '14px', fontWeight: '600' }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="btn-secondary"
                    style={{
                      padding: '10px 20px',
                      fontSize: '14px',
                      opacity: currentPage === totalPages ? 0.5 : 1,
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                    }}
                    aria-label="Next page"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showSaveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.95)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div className="card" style={{ maxWidth: '500px', width: '100%', margin: 0 }}>
            <h3 style={{ fontSize: '24px', marginBottom: '20px', color: '#98FB98', textShadow: '0 0 15px rgba(152, 251, 152, 0.7)' }}>
              &gt; Save This Search
            </h3>
            {saveMessage ? (
              <div style={{
                padding: '16px',
                background: saveMessage.includes('Error') || saveMessage.includes('already') ? 'rgba(139, 34, 34, 0.3)' : 'rgba(34, 139, 34, 0.3)',
                border: `2px solid ${saveMessage.includes('Error') || saveMessage.includes('already') ? '#FF6B6B' : '#98FB98'}`,
                borderRadius: '8px',
                color: saveMessage.includes('Error') || saveMessage.includes('already') ? '#FF6B6B' : '#98FB98',
                fontSize: '15px',
                fontWeight: '600',
                textAlign: 'center',
                marginBottom: '20px'
              }}>
                {saveMessage}
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <label className="label">Search Name</label>
                  <input
                    type="text"
                    className="input"
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    placeholder="e.g., Controller - Oklahoma City"
                    autoFocus
                  />
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => {
                      setShowSaveModal(false)
                      setSaveName('')
                      setAnalysisToSave(null)
                      setSaveMessage('')
                    }}
                    className="btn-secondary"
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmSaveSearch}
                    className="btn"
                    style={{ flex: 1 }}
                    disabled={!saveName.trim()}
                  >
                    Save
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
